/**
 * Connection-status Redux slice.
 *
 * Tracks the WebSocket connection state so UI components can display a
 * real-time status indicator (green / yellow / red).
 *
 * ### States
 * - `"connected"`    — STOMP session active, ticks flowing.
 * - `"connecting"`   — SockJS handshake / STOMP CONNECT in progress.
 * - `"disconnected"` — no active connection.
 * - `"reconnecting"` — automatic reconnect after a heartbeat timeout.
 *
 * The `websocket-handler.ts` service dispatches the appropriate actions
 * at each lifecycle transition.
 */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type ConnectionState = "connected" | "connecting" | "disconnected" | "reconnecting";

export interface ConnectionStatusState {
  /** Current WebSocket connection state. */
  status: ConnectionState;
  /** ISO timestamp of the last successful STOMP frame received. */
  lastDataAt: string | null;
  /** Number of reconnect attempts since last stable connection. */
  reconnectCount: number;
}

const initialState: ConnectionStatusState = {
  status: "disconnected",
  lastDataAt: null,
  reconnectCount: 0,
};

export const connectionStatusSlice = createSlice({
  name: "connectionStatus",
  initialState,
  reducers: {
    setConnectionStatus: (state, action: PayloadAction<ConnectionState>) => {
      state.status = action.payload;
      if (action.payload === "connected") {
        state.reconnectCount = 0;
      }
      if (action.payload === "reconnecting") {
        state.reconnectCount += 1;
      }
    },
    setLastDataAt: (state, action: PayloadAction<string>) => {
      state.lastDataAt = action.payload;
    },
    resetConnectionStatus: () => initialState,
  },
});

export const {
  setConnectionStatus,
  setLastDataAt,
  resetConnectionStatus,
} = connectionStatusSlice.actions;

export default connectionStatusSlice.reducer;
